// Activity Type Configuration Registry
export const ACTIVITY_TYPES = {
  SCHEDULED: 'scheduled',
  CHECKLIST: 'checklist',
  TASK: 'task',
  COMMENT: 'comment',
  NOTE: 'note',
  ATTACHMENT: 'attachment',
  EMAIL: 'email',
  CALL: 'call',
  // Sales Order specific types
  FINANCIAL: 'financial',
  STATUS: 'status',
  INVENTORY: 'inventory',
  ORDER: 'order',
  // Legacy system types
  LEGACY_LEDGER: 'legacy ledger',
  // Additional activity types
  ACCOUNT: 'account',
  BRAND: 'brand',
  CONTACT: 'contact',
  DOCUMENTATION: 'documentation',
  SYSTEM: 'system',
  LOGISTICS: 'logistics',
  ADMINISTRATIVE: 'administrative'
}

export const activityTypeConfig = {
  [ACTIVITY_TYPES.SCHEDULED]: {
    icon: 'mdi-calendar-clock',
    color: '#1976D2',
    label: 'Scheduled Event',
    component: 'ScheduledActivity',
    mapper: (activity) => ({
      dateRange: {
        start: activity.date?.start?.seconds
          ? new Date(activity.date.start.seconds * 1000)
          : new Date(),
        end: activity.date?.end?.seconds
          ? new Date(activity.date.end.seconds * 1000)
          : new Date(),
      },
    })
  },
  
  [ACTIVITY_TYPES.CHECKLIST]: {
    icon: 'mdi-checkbox-marked-circle',
    color: '#43A047',
    label: 'Checklist Item',
    component: 'ChecklistActivity',
    mapper: (activity) => ({
      item: activity.item,
      completed: activity.completed,
    })
  },
  
  [ACTIVITY_TYPES.TASK]: {
    icon: 'mdi-clipboard-check',
    color: '#009688',
    label: 'Task',
    component: 'TaskActivity',
    mapper: (activity) => activity
  },
  
  [ACTIVITY_TYPES.COMMENT]: {
    icon: 'mdi-comment-text',
    color: '#FF9800',
    label: 'Comment',
    component: 'CommentActivity',
    mapper: (activity) => ({
      item: activity.item,
    })
  },
  
  [ACTIVITY_TYPES.NOTE]: {
    icon: 'mdi-note-text',
    color: '#FF9800',
    label: 'Note',
    component: 'NoteActivity',
    mapper: (activity) => ({
      item: activity.item,
    })
  },
  
  [ACTIVITY_TYPES.ATTACHMENT]: {
    icon: 'mdi-paperclip',
    color: '#9C27B0',
    label: 'Attachment',
    component: 'AttachmentActivity',
    mapper: (activity) => ({
      fileName: activity.fileName,
      fileType: activity.fileType,
      previewUrl: activity.previewUrl,
      path: activity.path,
    })
  },
  
  [ACTIVITY_TYPES.EMAIL]: {
    icon: 'mdi-email',
    color: '#1565C0',
    label: 'Email',
    component: 'EmailActivity',
    mapper: (activity) => activity
  },
  
  [ACTIVITY_TYPES.CALL]: {
    icon: 'mdi-phone',
    color: '#2E7D32',
    label: 'Phone Call',
    component: 'CallActivity', 
    mapper: (activity) => activity
  },

  // Sales Order Activity Types
  [ACTIVITY_TYPES.FINANCIAL]: {
    icon: 'mdi-currency-usd',
    color: '#4CAF50',
    label: 'Financial',
    component: 'FinancialActivity',
    mapper: (activity) => activity
  },

  [ACTIVITY_TYPES.STATUS]: {
    icon: 'mdi-swap-horizontal',
    color: '#00BCD4',
    label: 'Status Change',
    component: 'StatusActivity',
    mapper: (activity) => activity
  },

  [ACTIVITY_TYPES.INVENTORY]: {
    icon: 'mdi-package-variant',
    color: '#FFC107',
    label: 'Inventory',
    component: 'InventoryActivity',
    mapper: (activity) => activity
  },

  [ACTIVITY_TYPES.ORDER]: {
    icon: 'mdi-receipt-text',
    color: '#673AB7',
    label: 'Order',
    component: 'OrderActivity',
    mapper: (activity) => activity
  },

  // Legacy system types
  [ACTIVITY_TYPES.LEGACY_LEDGER]: {
    icon: 'mdi-history',
    color: '#9E9E9E',
    label: 'Legacy Ledger',
    component: 'LegacyLedgerActivity',
    mapper: (activity) => ({
      item: activity.item || activity.action || 'Legacy activity',
      legacyData: activity.legacyData || {},
      isLegacy: true
    })
  },

  // Additional Activity Types
  [ACTIVITY_TYPES.ACCOUNT]: {
    icon: 'mdi-account',
    color: '#2196F3',
    label: 'Account',
    component: 'AccountActivity',
    mapper: (activity) => activity
  },

  [ACTIVITY_TYPES.BRAND]: {
    icon: 'mdi-tag',
    color: '#9C27B0',
    label: 'Brand',
    component: 'BrandActivity',
    mapper: (activity) => activity
  },

  [ACTIVITY_TYPES.CONTACT]: {
    icon: 'mdi-account-group',
    color: '#FF9800',
    label: 'Contact',
    component: 'ContactActivity',
    mapper: (activity) => activity
  },

  [ACTIVITY_TYPES.DOCUMENTATION]: {
    icon: 'mdi-file-document',
    color: '#9E9E9E',
    label: 'Documentation',
    component: 'DocumentationActivity',
    mapper: (activity) => activity
  },

  [ACTIVITY_TYPES.SYSTEM]: {
    icon: 'mdi-cog',
    color: '#3F51B5',
    label: 'System',
    component: 'SystemActivity',
    mapper: (activity) => activity
  },

  [ACTIVITY_TYPES.LOGISTICS]: {
    icon: 'mdi-truck',
    color: '#8BC34A',
    label: 'Logistics',
    component: 'LogisticsActivity',
    mapper: (activity) => activity
  },

  [ACTIVITY_TYPES.ADMINISTRATIVE]: {
    icon: 'mdi-account-tie',
    color: '#E91E63',
    label: 'Administrative',
    component: 'AdministrativeActivity',
    mapper: (activity) => activity
  }
}

// Helper functions
export const getActivityConfig = (type) => activityTypeConfig[type] || {}
export const getActivityTypes = () => Object.keys(activityTypeConfig)
export const getActivityIcon = (type) => getActivityConfig(type).icon || 'mdi-information'
export const getActivityColor = (type) => getActivityConfig(type).color || '#757575'
export const getActivityLabel = (type) => getActivityConfig(type).label || type
export const mapActivityData = (activity) => {
  const config = getActivityConfig(activity.type)
  
  // Handle different user field structures
  let userDisplay = 'Unknown User'
  if (activity.user) {
    if (typeof activity.user === 'string') {
      userDisplay = activity.user
    } else if (activity.user.name) {
      if (typeof activity.user.name === 'string') {
        userDisplay = activity.user.name
      } else if (activity.user.name.first && activity.user.name.last) {
        userDisplay = `${activity.user.name.first} ${activity.user.name.last}`
      }
    }
  }
  
  const baseActivity = {
    id: activity.id || '',
    date: activity.created && activity.created.toDate
      ? activity.created.toDate()
      : (activity.created && activity.created.seconds 
          ? new Date(activity.created.seconds * 1000)
          : new Date()),
    action: activity.action || '',
    type: activity.type || '',
    user: userDisplay,
    category: activity.category || '',
    item: activity.item || '',
    businessImpact: activity.businessImpact || '',
    changeData: activity.changeData || {},
    account: activity.account || {},
    brand: activity.brand || {},
    priority: activity.priority || '',
    relatedTo: activity.relatedTo || '',
    relatedID: activity.relatedID || '',
    attachments: activity.attachments || [],
    mentions: activity.mentions || [],
    isPinned: activity.isPinned || false,
    pinnedBy: activity.pinnedBy || null
  }
  
  return config.mapper 
    ? { ...baseActivity, ...config.mapper(activity) }
    : baseActivity
} 